import { Paper } from '@mantine/core';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { ListingDeliveryTypeDisplay } from 'src/components/Listings/ListingDeliveryTypeDisplay';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import {
  MobileListingAutoPricingEnabledDisplay,
  MobileListingNotesDisplay,
  MobileListingSeatSaverDisplay,
} from 'src/contexts/MobileListingDrawerContext/components/MobileListingIconDisplay';
import { useMobileListingDrawerContext } from 'src/contexts/MobileListingDrawerContext/MobileListingDrawerContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { useSellerAccountContext } from 'src/contexts/SellerAccountContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatOption } from 'src/modals/EditTableColumns';
import { LaunchBroadcastListing } from 'src/modals/ListingDetails/actions/LaunchBroadcastListing';
import {
  getAllInPriceFromListPrice,
  getFlattenedListingIds,
} from 'src/utils/inventoryUtils';
import { Event, Feature, Listing } from 'src/WebApiController';

import * as styles from './ListingTilesV2.css';

export const ListingTile = ({
  listing,
  entities,
  event,
}: {
  listing: Listing;
  entities: Listing[];
  event: Event;
}) => {
  const { getSelection, setGroupItems, getGroupToggleState } =
    useMultiSelectionContext();
  const listingSelection = useMemo(
    () => getSelection(event.virtId),
    [event.virtId, getSelection]
  );

  const isGroupSelected = useMemo(
    () =>
      getGroupToggleState(event.virtId).isGroupSelected &&
      !listingSelection.items.itemIds.length,
    [event.virtId, getGroupToggleState, listingSelection.items.itemIds.length]
  );
  const isSelected = useMemo(() => {
    return (
      isGroupSelected ||
      listingSelection.items.itemIds.includes(String(listing.id))
    );
  }, [isGroupSelected, listingSelection.items.itemIds, listing.id]);

  const { setActivePosEntity } = useActivePosEntityContext<Listing>();

  const toggleListingSelection = useCallback(() => {
    const listingIdsToggled = [listing.id];

    const selectedListingIds = isGroupSelected
      ? getFlattenedListingIds(entities).map((id) => String(id))
      : listingSelection.items.itemIds;

    // Current intended behavior: Either select all ids in listingGroup or none
    if (
      selectedListingIds.some((selected) =>
        listingIdsToggled.includes(parseInt(selected))
      )
    ) {
      // Deselect
      setGroupItems(
        event.virtId,
        selectedListingIds
          .map((id) => parseInt(id))
          .filter((id) => !listingIdsToggled.includes(id))
          .reduce(
            (r, l) => {
              r[l] = true;
              return r;
            },
            {} as Record<number, boolean>
          )
      );
    } else {
      // Add select
      selectedListingIds.push(...listingIdsToggled.map((lId) => String(lId)));
      setGroupItems(
        event.virtId,
        selectedListingIds
          .map((id) => parseInt(id))
          .reduce(
            (r, listingId) => {
              r[listingId] = true;
              return r;
            },
            {} as Record<number, boolean>
          )
      );
    }
  }, [
    entities,
    event.virtId,
    isGroupSelected,
    listing,
    listingSelection.items.itemIds,
    setGroupItems,
  ]);

  const { onSelectListing, toggleEditingListing } =
    useMobileListingDrawerContext();

  const hasPricedByColumnFeature = useUserHasFeature(Feature.PricedByColumn);

  const onViewClick = useCallback(async () => {
    if (listingSelection.isSingleGroupMultiSelect) {
      toggleListingSelection();
      return;
    }

    // Only allow click when not a listing group
    setActivePosEntity(listing.id, listing.idOnMkp, true);
    onSelectListing(listing);
  }, [
    listingSelection.isSingleGroupMultiSelect,
    setActivePosEntity,
    listing,
    toggleListingSelection,
    onSelectListing,
  ]);

  const onPriceInputClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (listingSelection.isSingleGroupMultiSelect || !listing.availQty) {
        return;
      }
      toggleEditingListing(listing);
    },
    [listing, listingSelection.isSingleGroupMultiSelect, toggleEditingListing]
  );

  const unitCost = useMemo(
    () => listing.unitCst?.disp ?? listing.faceValue?.disp,
    [listing.faceValue?.disp, listing.unitCst?.disp]
  );

  const { allActiveUserInfos } = useSellerAccountContext();
  const pricerName = useMemo(
    () =>
      listing.pricerId
        ? (allActiveUserInfos?.[listing.pricerId]?.displayName ?? null)
        : null,
    [allActiveUserInfos, listing.pricerId]
  );

  return (
    <Paper
      withBorder
      p={vars.spacing.m}
      radius={vars.borderRadius.m}
      onClick={onViewClick}
      bg={vars.color.backgroundPrimary}
      style={{ color: vars.color.textPrimary, borderColor: vars.color.border }}
      className={clsx({ [styles.tileSelected]: isSelected })}
    >
      <Stack direction="row" alignItems="center" justifyContent="spaceBetween">
        <Stack direction="row" alignItems="center" gap="m">
          {listingSelection.isSingleGroupMultiSelect && (
            <div
              onClick={(e) => {
                /* Prevent onViewClick from being called twice and interfering with selection */
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Checkbox
                checked={isSelected}
                checkboxSizeVariant="light"
                onChange={onViewClick}
              />
            </div>
          )}
          <Stack direction="column" gap="m">
            <SeatingInfo
              {...listing.seating}
              fontSize={vars.typography.fontSize.base}
              labelClassName={styles.seatingLabel}
            />
            <Stack direction="row" alignItems="center" gap="s">
              <MobileListingAutoPricingEnabledDisplay
                isAutoPricingEnabled={listing.isAutoPrc}
              />
              <MobileListingSeatSaverDisplay
                isSeatSaver={listing.isSeatSaver}
              />
              <MobileListingNotesDisplay listingNotes={listing.seatTraits} />
              <ListingDeliveryTypeDisplay
                listing={listing}
                userDefinedPrecision={FormatOption.EnumDisplay_Icon}
                showIconTitle={false}
                iconContainerClassName={styles.deliveryIconContainer}
              />
              {unitCost && (
                <div className={styles.priceLabel}>
                  <span
                    className={styles.secondaryInfo}
                  >{`(${unitCost})`}</span>
                </div>
              )}
              {hasPricedByColumnFeature && pricerName && (
                <span className={styles.secondaryInfo}>{pricerName}</span>
              )}
            </Stack>
          </Stack>
        </Stack>
        <div style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>
          <PriceInputButton
            event={event}
            listing={listing}
            isSingleGroupMultiSelect={listingSelection.isSingleGroupMultiSelect}
            onClick={onPriceInputClick}
          />
        </div>
      </Stack>
    </Paper>
  );
};

const PriceInputButton = ({
  event,
  listing,
  isSingleGroupMultiSelect,
  onClick,
}: {
  event: Event;
  listing: Listing;
  isSingleGroupMultiSelect: boolean;
  onClick: (e: React.MouseEvent | React.TouchEvent) => void;
}) => {
  const { getUiCurrency } = useLocalizationContext();
  const uiCurrency = getUiCurrency(listing.currency);

  const fixedPrice = useMemo(() => {
    const value =
      getAllInPriceFromListPrice(listing.listPrice, listing) ?? undefined;
    if (value == null) {
      return '';
    }
    const numericalValue = parseFloat((value ?? 0).toString());
    return numericalValue.toFixed(uiCurrency.dec);
  }, [listing, uiCurrency.dec]);

  const disabled = useMemo(
    () => isSingleGroupMultiSelect || !listing.availQty,
    [isSingleGroupMultiSelect, listing.availQty]
  );

  return (
    <Stack
      role="button"
      width="full"
      alignItems="center"
      justifyContent="spaceBetween"
      onClick={(e) => !disabled && onClick(e)}
      className={styles.priceInputBox}
    >
      <Stack alignItems="center">
        <span>{uiCurrency.sym}</span>
      </Stack>
      <Stack alignItems="center" gap="s">
        <span>{fixedPrice}</span>
        <LaunchBroadcastListing
          forTable={true}
          event={event}
          listing={listing}
          lockListingGroupFromEdits={undefined}
          iconOnlyMode={true}
          disabled={disabled}
        />
      </Stack>
    </Stack>
  );
};
